import { db, type OutboxItem } from './db.ts'

/**
 * The draft queue. Records created with no signal wait here and are replayed,
 * in creation order, when the connection returns. Each carries a client-generated
 * request id so the server can upsert it — a replay that half-succeeded and runs
 * again produces the same single record (blueprint §4.9).
 *
 * The one subtlety is dependency: a note or a draft offer created offline for a
 * client that was ALSO created offline references a local id the server has never
 * seen. Replaying the client first yields its real id; later items in the queue
 * that pointed at the local id are rewritten to the real one before they POST.
 */

function uuid(): string {
  return crypto.randomUUID()
}

export function pendingCount(): Promise<number> {
  return db.outbox.where('status').equals('pending').count()
}

/**
 * Puts a create in the queue. Returns the ids the UI needs: the request id (for
 * traceability) and, for an entity create, a local id it can hand to dependent
 * records made in the same offline session.
 */
export async function enqueue(
  kind: OutboxItem['kind'],
  path: string,
  body: Record<string, unknown>,
): Promise<{ clientRequestId: string; localEntityId?: string }> {
  const clientRequestId = uuid()
  const localEntityId = kind === 'client' ? uuid() : undefined
  await db.outbox.add({
    kind,
    path,
    body: { ...body, clientRequestId },
    clientRequestId,
    ...(localEntityId ? { localEntityId } : {}),
    status: 'pending',
    createdAt: Date.now(),
  })
  return { clientRequestId, ...(localEntityId ? { localEntityId } : {}) }
}

let syncing = false

/**
 * Replays the queue. Safe to call repeatedly (a lock prevents overlap) and safe
 * to interrupt — an item that fails on the network is left pending and retried
 * on the next run. Idempotent server-side, so a retry never doubles a record.
 */
export async function syncOutbox(): Promise<{ sent: number; failed: number }> {
  if (syncing || !navigator.onLine) return { sent: 0, failed: 0 }
  syncing = true
  let sent = 0
  let failed = 0
  // Maps a local entity id (from an offline-created client) to the real server id.
  const resolved = new Map<string, string>()
  try {
    // Seed from entities that already synced (possibly in an earlier run) — a note
    // deferred to now must still find its client's real id.
    const all = await db.outbox.toArray()
    for (const i of all) if (i.localEntityId && i.serverId) resolved.set(i.localEntityId, i.serverId)

    const items = await db.outbox.where('status').equals('pending').sortBy('id')
    // Every local id ever minted for an offline-created client in this queue.
    const localIds = new Set(all.map((i) => i.localEntityId).filter(Boolean) as string[])
    for (const item of items) {
      let path = item.path
      let bodyJson = JSON.stringify(item.body)

      // A local reference to an offline-created client can appear in the URL
      // (/api/clients/<id>/notes) OR the body (customerId). Substitute every
      // resolved id in both, so the dependent record points at the real client.
      for (const [localId, serverId] of resolved) {
        if (path.includes(localId)) path = path.split(localId).join(serverId)
        if (bodyJson.includes(localId)) bodyJson = bodyJson.split(localId).join(serverId)
      }
      // Still referencing an offline client that hasn't been replayed yet? It sits
      // earlier in id order, so this is only reached if that client failed — leave
      // this one pending too rather than POST a dangling reference.
      const stillDangling = [...localIds].some((id) => !resolved.has(id) && (path.includes(id) || bodyJson.includes(id)))
      if (item.kind !== 'client' && stillDangling) continue

      try {
        const res = await fetch(path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: bodyJson,
        })
        if (!res.ok) {
          // A 4xx means the server rejected it — parking it forever would hide the
          // problem. Mark done with the error so it leaves the pending count; the
          // record is in neither place, which the sync report surfaces.
          if (res.status >= 400 && res.status < 500) {
            const msg = await res.text().catch(() => '')
            await db.outbox.update(item.id!, { status: 'done', error: msg.slice(0, 300) })
            failed++
            continue
          }
          // 5xx / offline mid-flight: stop, keep it pending, try again later.
          break
        }
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
        const serverId = extractId(item.kind, data)
        if (item.localEntityId && serverId) resolved.set(item.localEntityId, serverId)
        // Persist the mapping on the item so a later run can still resolve it.
        await db.outbox.update(item.id!, { status: 'done', ...(item.localEntityId && serverId ? { serverId } : {}) })
        sent++
      } catch {
        // Network dropped again — leave the rest for the next run.
        break
      }
    }
  } finally {
    syncing = false
  }
  return { sent, failed }
}

/**
 * Drops sent items so the queue doesn't grow forever — but only once NOTHING is
 * pending, so a still-queued note can't lose the client-id mapping it needs.
 */
export async function pruneOutbox(): Promise<void> {
  if ((await pendingCount()) > 0) return
  await db.outbox.where('status').equals('done').delete()
}

function extractId(kind: OutboxItem['kind'], data: Record<string, unknown>): string | undefined {
  const key = kind === 'client' ? 'client' : kind === 'quote' ? 'quote' : 'note'
  const obj = data[key]
  if (obj && typeof obj === 'object' && 'id' in obj) return String((obj as { id: unknown }).id)
  return undefined
}
